import { useState } from 'react'

import { format } from 'date-fns'
import { FormattedMessage } from 'react-intl'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import SubmissionCardHeaderAvatar from '@components/Submission/Card/Header/Avatar/Avatar'
import SubmissionCardHeaderHandle from '@components/Submission/Card/Header/Handle/Handle'
import SubHeaderIconWrapper from '@components/Submission/Card/Header/SubHeader/SocialIconWrapper'
import SubmissionCardSubHeader from '@components/Submission/Card/Header/SubHeader/SubHeader'
import { TextThumbnailPreview } from '@components/Submission/Card/TextPreview/TextPreview'
import { getThumbnailIcon } from '@components/Submission/Card/Thumbnail/Thumbnail'
import Image from '@components/UI/Image'
import { AspectRatios } from '@enums'
import { IApplicationState } from '@store/index'
import { IVersion, selectIsTextSubmission, selectSubmission, viewSocial } from '@store/submissions'
import Theme from '@theme'
import { Container, FadeIn, H1, Icon, Link, Text } from '@tribegroup'
import { getSubmissionFlags } from '@utils/getSubmissionFlags'
import { ThreeLineTextContainer } from './CompareSubmission.styled'
import { NavigationButton } from './PitchComparisonModal.styled'

type SubmissionPreviewProps = {
  headerMessageId: string
  submissionId: number
  campaignId: number
  version: IVersion
  timestampMessageId: string
}

export const CompareSubmissionPreview: React.FC<SubmissionPreviewProps> = ({
  headerMessageId,
  submissionId,
  campaignId,
  version,
  timestampMessageId,
}) => {
  const { iconGlyph, isTextSubmission } = useSelector((state: IApplicationState) => {
    const submission = selectSubmission(state, submissionId)
    const submissionFlags = getSubmissionFlags(submission)

    return {
      iconGlyph: submission.social_submission_type
        ? getThumbnailIcon(submissionFlags, submission.social_submission_type)
        : 'content-campaign',
      isTextSubmission: selectIsTextSubmission(state, submissionId),
    }
  }, shallowEqual)

  const dispatch = useDispatch()
  const [currentFrame, setFrame] = useState(0)
  const isMultipleFrames = version.expected_number_of_frames > 1
  const maximumFrames = version.expected_number_of_frames
  const onViewSocial = () => {
    dispatch(viewSocial(campaignId, submissionId))
  }
  const currentMediaObject = version.media_objects[currentFrame]
  return (
    <Container>
      <H1 color={Theme.defaultColor} center bottomOuterSpacing={1}>
        <FormattedMessage id={headerMessageId} />
      </H1>
      <Container
        boxShadow="0px 8px 20px 0px rgba(0, 0, 0, 0.1)"
        borderRadius={0.5}
        relative
        backgroundColor={Theme.whiteColor}
      >
        <Container flexStart innerSpacingX={1} innerSpacingY={0.75}>
          <SubmissionCardHeaderAvatar campaignId={campaignId} submissionId={submissionId} />
          <Container flexStart flexColumn alignItems="flex-start">
            <SubmissionCardHeaderHandle
              campaignId={campaignId}
              submissionId={submissionId}
              onViewSocial={onViewSocial}
            />
            <SubHeaderIconWrapper
              campaignId={campaignId}
              submissionId={submissionId}
              onViewSocial={onViewSocial}
            >
              <SubmissionCardSubHeader campaignId={campaignId} submissionId={submissionId} />
            </SubHeaderIconWrapper>
          </Container>
        </Container>

        {isTextSubmission && version.text ? (
          <TextThumbnailPreview content={version.text} />
        ) : (
          <Link
            key={currentMediaObject.id}
            href={currentMediaObject.original_media_url}
            target="_blank"
          >
            <Image
              src={currentMediaObject.thumbnail_media_url}
              aspectRatio={AspectRatios.ONE_BY_ONE}
              animation={FadeIn}
              loadingColor={Theme.whiteColor}
              aria-label={`Frame ${currentMediaObject.frame + 1}`}
            />
          </Link>
        )}
        {isMultipleFrames && (
          <>
            {currentFrame > 0 && (
              <NavigationButton style={{ left: '1rem' }} onClick={() => setFrame(currentFrame - 1)}>
                <Icon glyph="nav-back-short" size={1.3} color={Theme.grey900Color} />
              </NavigationButton>
            )}
            {currentFrame < maximumFrames - 1 && (
              <NavigationButton
                style={{ right: '1rem' }}
                onClick={() => setFrame(currentFrame + 1)}
              >
                <Icon glyph="nav-fwd-short" size={1.3} color={Theme.grey900Color} />
              </NavigationButton>
            )}
          </>
        )}
        {iconGlyph && (
          <Container absolute rightPosition={1} topPosition={5}>
            <Icon size={1.5} glyph={iconGlyph} color={Theme.whiteColor} shadowed />
          </Container>
        )}
        <Container innerSpacingX={1} innerSpacingY={0.75}>
          {version.description && (
            <ThreeLineTextContainer bottomOuterSpacing={0.75} preWrap>
              <Text color={Theme.defaultColor}>{version.description}</Text>
            </ThreeLineTextContainer>
          )}
          <Text color={Theme.grey800Color} uppercase xsmall>
            <FormattedMessage
              id={timestampMessageId}
              values={{
                date: format(new Date(version.created_at), 'd MMM yyyy'),
                time: format(new Date(version.created_at), 'p'),
              }}
            />
          </Text>
        </Container>
      </Container>
      {currentMediaObject?.note && (
        <Container flexStart topOuterSpacing={1.25}>
          <Container
            innerSpacing={0.5}
            rightOuterSpacing={1}
            minWidth={1.5}
            flexStart
            borderRadius={'50%'}
            backgroundColor={Theme.whiteColor}
          >
            <Icon glyph="notes-creators" size={1.5} />
          </Container>
          <ThreeLineTextContainer preWrap>
            <Text color={Theme.defaultColor}>{currentMediaObject.note}</Text>
          </ThreeLineTextContainer>
        </Container>
      )}
    </Container>
  )
}
